import { useEffect, useMemo } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import { storeStripePublishableKey } from 'frontastic/hooks/useCheckout/resumeTokenStorage';
import { StripeFormProviderProps } from './types';

/**
 * @remarks
 * Normally these styling values would come from Paragon, however since stripe
 * requires styling to be passed in through the appearance object they are
 * currently placed here.
 */
const ELEMENTS_APPEARANCE = {
  rules: {
    '.Input': {
      border: 'solid 1px #707070', // $gray-500
      borderRadius: '0',
      fontSize: '1.125rem',
      lineHeight: '1.33334rem',
    },
    '.Input--invalid': {
      border: '1px solid #D23228',
      boxShadow: 'none',
    },
    '.Input:hover': {
      border: '1px solid #1f3226',
    },
    '.Input:focus': {
      color: '#454545',
      backgroundColor: '#FFFFFF', // $white
      borderColor: '#0A3055', // $primary
      outline: '0',
      boxShadow: '0 0 0 1px #0A3055', // $primary
    },
    '.Label': {
      fontSize: '1.125rem',
      fontFamily: 'Inter,Helvetica Neue,Arial,sans-serif',
      fontWeight: '400',
      marginBottom: '0.5rem',
      color: '#454545',
    },
    '.Error': {
      fontSize: '0.875rem',
      fontWeight: '400',
      color: '#D23228',
    },
    '.Text--terms': {
      fontFamily: 'Inter,Helvetica Neue,Arial,sans-serif',
      fontWeight: '400',
      color: '#454545',
      fontSize: '18px',
      lineHeight: '28px',
    },
  },
} as const satisfies StripeElementsOptions['appearance'];

export const StripeFormProvider = ({
  stripePublishableKey,
  options,
  children,
}: StripeFormProviderProps) => {
  const stripePromise = useMemo(
    () => loadStripe(stripePublishableKey ?? ''),
    [stripePublishableKey],
  );

  useEffect(() => {
    if (stripePublishableKey) {
      storeStripePublishableKey(stripePublishableKey);
    }
  }, [stripePublishableKey]);

  const stripeOptions = {
    appearance: ELEMENTS_APPEARANCE,
    ...options,
  } as StripeElementsOptions;

  return stripePublishableKey ? (
    <Elements stripe={stripePromise} options={stripeOptions}>
      {children}
    </Elements>
  ) : null;
};
